import React, {useCallback, useEffect, useState} from 'react';
import {Autocomplete, Banner, BlockStack, ChoiceList, Modal, Spinner, Text, Thumbnail,} from '@shopify/polaris';
import {bulkCreateRecommendationsAPI, fetchAISuggestionsAPI, fetchProductsAPI} from "../../api/axiosRequests";

interface ProductOption {
    label: string;
    value: string;
    imageSrc?: string;
}

interface Props {
    open: boolean;
    shop: string | null;
    existingPairs: { product_id: number; recommended_product_id: number }[];
    onClose: () => void;
    onSaved: () => void;
}

export function BulkRecommendationModal({open, shop, existingPairs, onClose, onSaved}: Props) {
    const [sourceId, setSourceId] = useState('');
    const [recommendedIds, setRecommendedIds] = useState<string[]>([]);
    const [sourceSearch, setSourceSearch] = useState('');
    const [recommendedSearch, setRecommendedSearch] = useState('');
    const [sourceOptions, setSourceOptions] = useState<ProductOption[]>([]);
    const [recommendedOptions, setRecommendedOptions] = useState<ProductOption[]>([]);
    const [loadingSource, setLoadingSource] = useState(false);
    const [loadingRecommended, setLoadingRecommended] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [recommendationType, setRecommendationType] = useState<'manual' | 'ai'>('manual');
    const [aiSuggestions, setAiSuggestions] = useState<ProductOption[]>([]);
    const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);

    const debounce = (func: Function, delay: number) => {
        let timeout: ReturnType<typeof setTimeout>;
        return (...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    const fetchProductOptions = async (term: string): Promise<ProductOption[]> => {
        if (!shop) return [];
        const res = await fetchProductsAPI(shop, term);
        return res.data.map((p: any) => ({
            label: p.title,
            value: p.shopify_product_id.toString(),
            imageSrc: p.images?.[0] || undefined,
        }));
    };

    const loadSources = useCallback(
        debounce(async (term: string) => {
            setLoadingSource(true);
            try {
                const options = await fetchProductOptions(term);
                setSourceOptions(options);
            } finally {
                setLoadingSource(false);
            }
        }, 300),
        [shop]
    );

    const loadRecommendations = useCallback(
        debounce(async (term: string) => {
            setLoadingRecommended(true);
            try {
                const options = await fetchProductOptions(term);
                const filtered = !sourceId
                    ? options
                    : options.filter(
                        (opt) =>
                            !existingPairs.some(
                                (pair) =>
                                    pair.product_id.toString() === sourceId &&
                                    pair.recommended_product_id.toString() === opt.value
                            )
                    );
                setRecommendedOptions(filtered);
            } finally {
                setLoadingRecommended(false);
            }
        }, 300),
        [shop, sourceId, existingPairs]
    );

    useEffect(() => {
        async function fetchAiSuggestions() {
            if (recommendationType === 'ai' && sourceId && shop) {
                setLoadingAiSuggestions(true);
                try {
                    const res = await fetchAISuggestionsAPI(shop, sourceId);
                    const suggestions = Array.isArray(res.data)
                        ? res.data.map((p: any) => ({
                            label: p.title ?? p.name ?? `Product ${p.product_id || ''}`,
                            value: p.shopify_product_id?.toString() || p.product_id?.toString() || '',
                            imageSrc: p.images?.[0] || undefined,
                        }))
                        : [];
                    setAiSuggestions(suggestions);
                } catch (err) {
                    setAiSuggestions([]);
                    console.error('Error fetching AI suggestions:', err);
                } finally {
                    setLoadingAiSuggestions(false);
                }
            } else {
                setAiSuggestions([]);
                setLoadingAiSuggestions(false);
            }
        }

        (async () => await fetchAiSuggestions())();
    }, [recommendationType, sourceId, shop]);

    useEffect(() => {
        if (open) {
            setSourceId('');
            setRecommendedIds([]);
            setSourceSearch('');
            setRecommendedSearch('');
            setSourceOptions([]);
            setRecommendedOptions([]);
            setError(null);
            setRecommendationType('manual');
            setAiSuggestions([]);
            loadSources('');
            loadRecommendations('');
        }
    }, [open]);

    const getLabel = (id: string, options: ProductOption[]) =>
        options.find((opt) => opt.value === id)?.label || '';

    const getImage = (id: string, options: ProductOption[]) =>
        options.find((opt) => opt.value === id)?.imageSrc;

    const handleSave = async () => {
        setError(null);
        if (!sourceId || recommendedIds.length === 0 || !shop) {
            setError('Please select 1 source and at least 1 recommended product.');
            return;
        }

        setLoading(true);
        try {
            const res = await bulkCreateRecommendationsAPI(
                shop,
                Number(sourceId),
                recommendedIds.map(Number),
                recommendationType
            );

            onSaved();
            onClose();
        } catch (err: any) {
            if (err.response) {
                setError(err.response.data.message || 'Bulk create failed');
            } else if (err.request) {
                setError('No response from the server. Please try again.');
            } else {
                setError(err.message || 'Unexpected error during save.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Add/remove recommended IDs for AI suggestions list
    const toggleAiRecommendation = (val: string) => {
        setRecommendedIds((prev) =>
            prev.includes(val) ? prev.filter((id) => id !== val) : [...prev, val]
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Create Recommendations"
            primaryAction={{
                content: loading ? 'Saving...' : 'Create',
                onAction: handleSave,
                disabled: loading || !sourceId || recommendedIds.length === 0,
            }}
            secondaryActions={[{content: 'Cancel', onAction: onClose}]}
        >
            <Modal.Section>
                <BlockStack gap={'025'}>
                    {error && (
                        <Banner title="Error" tone="critical" onDismiss={() => setError(null)}>
                            {error}
                        </Banner>
                    )}

                    {/* Recommendation Type Selector */}
                    <ChoiceList
                        choices={[
                            {label: 'Manual', value: 'manual'},
                            {label: 'AI Suggestions', value: 'ai'},
                        ]}
                        selected={[recommendationType]}
                        onChange={(selected) => setRecommendationType(selected[0] as 'manual' | 'ai')}
                        title="Recommendation Type"
                        allowMultiple={false}
                    />

                    {/* Source */}
                    <Autocomplete
                        options={sourceOptions}
                        selected={sourceId ? [sourceId] : []}
                        onSelect={([val]) => setSourceId(val)}
                        textField={
                            <Autocomplete.TextField
                                label="Source Product"
                                onFocus={() => {
                                    if (sourceOptions.length <= 1) loadSources('');
                                }}
                                onChange={(val) => {
                                    setSourceSearch(val);
                                    loadSources(val);
                                }}
                                value={getLabel(sourceId, sourceOptions) || sourceSearch}
                                placeholder="Search products..."
                                autoComplete="off"
                            />
                        }
                        loading={loadingSource}
                    />

                    {/* Preview Source */}
                    {sourceId && (
                        <div style={{display: 'flex', marginTop: 16, alignItems: 'center', gap: 8}}>
                            <Thumbnail source={getImage(sourceId, sourceOptions) || ''} size="small" alt="source"/>
                            <Text as={'p'} tone="subdued">
                                {getLabel(sourceId, sourceOptions)}
                            </Text>
                        </div>
                    )}

                    {/* Recommended / AI Suggestions */}
                    {recommendationType === 'manual' ? (
                        <>
                            <Autocomplete
                                options={recommendedOptions}
                                selected={recommendedIds}
                                onSelect={(vals) => setRecommendedIds(vals)}
                                allowMultiple
                                textField={
                                    <Autocomplete.TextField
                                        label="Recommended Products"
                                        onFocus={() => {
                                            if (recommendedOptions.length <= 1) loadRecommendations('');
                                        }}
                                        onChange={(val) => {
                                            setRecommendedSearch(val);
                                            loadRecommendations(val);
                                        }}
                                        value={recommendedSearch}
                                        placeholder="Search and select multiple products..."
                                        autoComplete="off"
                                    />
                                }
                                loading={loadingRecommended}
                            />
                            {/* Preview selected recommended */}
                            {recommendedIds.map((id) => (
                                <div key={id} style={{display: 'flex', marginTop: 16, alignItems: 'center', gap: 8}}>
                                    <Thumbnail source={getImage(id, recommendedOptions) || ''} size="small" alt={'Recommended Image'}/>
                                    <Text as={'p'} tone="subdued">
                                        {getLabel(id, recommendedOptions)}
                                    </Text>
                                </div>
                            ))}
                        </>
                    ) : (
                        <>
                            {loadingAiSuggestions ? (
                                <div style={{marginTop: 16, textAlign: 'center'}}>
                                    <Spinner size="small" accessibilityLabel="Loading AI suggestions" />
                                </div>
                            ) : aiSuggestions.length === 0 ? (
                                <div style={{marginTop: 16}}>
                                    <Text tone="subdued" variant="bodySm" as="p">
                                        No suggestions as of now
                                    </Text>
                                </div>
                            ) : (
                                aiSuggestions.map((p) => (
                                    <div
                                        key={p.value}
                                        onClick={() => toggleAiRecommendation(p.value)}
                                        style={{
                                            cursor: 'pointer',
                                            marginTop: 8,
                                            padding: 8,
                                            backgroundColor: recommendedIds.includes(p.value) ? '#e3fcef' : 'transparent',
                                            borderRadius: 6,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        {p.imageSrc && <Thumbnail source={p.imageSrc} alt={p.label} size="small" />}
                                        <Text as="p" variant="bodyMd">
                                            {p.label}
                                        </Text>
                                    </div>
                                ))
                            )}
                        </>
                    )}
                </BlockStack>
            </Modal.Section>
        </Modal>
    );
}
